import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LogIn } from 'lucide-react';
import toast from 'react-hot-toast';

export function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Redirect to home if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    try {
      setIsLoggingIn(true);
      await login();
      toast.success('Successfully logged in!');
      // Navigation will happen via useEffect when isAuthenticated changes
    } catch (error: unknown) {
      console.error('Login failed:', error);
      
      // Extract meaningful error message from MSAL errors or Axios errors
      const msalError = error as { 
        errorCode?: string; 
        errorMessage?: string; 
        message?: string;
        name?: string;
        response?: {
          status?: number;
          data?: { 
            error?: { message?: string };
            message?: string;
          };
        };
      };
      
      let errorMessage = 'Login failed. Please try again.';
      
      // Check for Axios/API errors first (token exchange failures)
      if (msalError.response) {
        const status = msalError.response.status;
        const serverMessage = msalError.response.data?.error?.message || msalError.response.data?.message;
        errorMessage = `Login failed (${status}): ${serverMessage || 'Server error during authentication'}`;
        console.error('API Error:', { status, serverMessage, data: msalError.response.data });
      } else if (msalError.errorCode) {
        // MSAL specific error
        errorMessage = `Login failed: ${msalError.errorCode}`;
        if (msalError.errorMessage) {
          errorMessage += ` - ${msalError.errorMessage}`;
        }
        
        // Check for common MSAL errors
        if (msalError.errorCode === 'user_cancelled') {
          errorMessage = 'Login was cancelled.';
        } else if (msalError.errorCode === 'popup_window_error') {
          errorMessage = 'Could not open login popup. Please allow popups for this site.';
        } else if (msalError.errorCode === 'redirect_uri_mismatch') {
          errorMessage = 'Configuration error: Redirect URI mismatch. Please contact support.';
        } else if (msalError.errorCode === 'invalid_client') {
          errorMessage = 'Configuration error: Invalid client ID. Please contact support.';
        }
      } else if (msalError.message) {
        errorMessage = `Login failed: ${msalError.message}`;
      }
      
      toast.error(errorMessage);
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-primary-600 dark:text-primary-400 mb-2">
            FinanceHub
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Centralized Finance Operations Platform
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Welcome Back
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Sign in with your organizational account
            </p>
          </div>

          {/* SSO Login Button */}
          <button
            onClick={handleLogin}
            disabled={isLoggingIn}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:cursor-not-allowed"
          >
            {isLoggingIn ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <LogIn className="h-5 w-5" />
                <span>Sign in with Microsoft</span>
              </>
            )}
          </button>

          <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>Secured by Azure Entra ID</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
          <p>© {new Date().getFullYear()} FinanceHub. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
